import { gql } from 'apollo-angular';
import {
  baseResponseFields,
  basePageableGraphqlField,
} from '../../../../graphql/baseGraphql.queries';

const institutionFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  name
  shortForm
  voteCode
  institutionType
  parentInstitution {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    name
    shortForm
    voteCode
    institutionType
    sectorName
    departmentCount
    childInstitutionCount
  }
  sectorName
  departmentCount
  childInstitutionCount
`;

const financialYearFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  startYear
  endYear
  isCurrent
  isClosed
  code
`;

const projectFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  name
  code
  description
  projectType
  institution {
    ${institutionFields}
  }
  implementingAgency {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    name
    shortForm
    voteCode
    institutionType
    sectorName
    departmentCount
    childInstitutionCount
  }
  sector
  subSector
  priorityLevel
  totalBudget
  budgetCurrency
  startDate
  plannedEndDate
  actualEndDate
  financialYear {
    ${financialYearFields}
  }
  region
  district
  status
  createdBy {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    email
    firstName
    lastName
    username
    userType
    institutionUserType
    lastLogin
    role {
      id
      uniqueId
      createdAt
      updatedAt
      deletedAt
      isDeleted
      isActive
      name
      scope
      institutionId
      institutionName
      permissions {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        name
        code
        scope
      }
    }
    institutionName
    departmentName
  }
`;

const projectPhaseFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  name
  phaseType
  phaseOrder
  weightPercent
  startDate
  endDate
  actualStart
  actualEnd
  status
`;

const subComponentsFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  code
  title
  description
  weightPercent
  unit
  plannedQuantity
  unitRate
  plannedStart
  plannedEnd
  actualStart
  actualEnd
  sortOrder
  status
`;

const roleFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  name
  scope
  institutionId
  institutionName
  permissions {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    name
    code
    scope
  }
`;

const designationFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  title
  description
  institutionName
`;

const userFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  email
  firstName
  lastName
  username
  userType
  institutionUserType
  lastLogin
  role {
    ${roleFields}
  }
  institutionName
  departmentName
  institution {
    ${institutionFields}
  }
`;

const minUserFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  email
  firstName
  lastName
  username
  userType
  institutionUserType
  lastLogin
  institutionName
  departmentName
`;

const projectComponentFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  phase {
    id
    uniqueId
    name
    phaseType
  }
  parent {
    id
    uniqueId
    code
    title
  }
  site {
    id
    uniqueId
    name
  }
  code
  title
  description
  weightPercent
  unit
  plannedQuantity
  unitRate
  plannedStart
  plannedEnd
  actualStart
  actualEnd
  responsible {
    ${userFields}
  }
  sortOrder
  status
  subComponents {
    ${subComponentsFields}
  }
`;

const projectMilestoneFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  phase {
    id
    uniqueId
    name
    phaseType
  }
  component {
    id
    uniqueId
    code
    title
  }
  title
  description
  milestoneType
  plannedDate
  actualDate
  evidenceRequired
  isCritical
  status
  achievedBy {
    ${userFields}
  }
  notes
`;

const projectComponentProgressField = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  component {
    id
    uniqueId
    code
    title
  }
  reportingPeriod {
    id
    uniqueId
    name
  }
  progressPercent
  actualQuantity
  narrative
  challenges
  source
  submittedBy {
    ${userFields}
  }
  status
`;

const projectBudgetlineFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  category
  description
  plannedAmount
  revisedAmount
  notes
`;

const ProjectExpenditureFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  budgetLine {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    category
    description
    plannedAmount
    revisedAmount
    notes
  }
  component {
    id
    uniqueId
    code
    title
  }
  contractorLink {
    id
    uniqueId
    contractNumber
    contractType
    companyName
  }
  expenditureDate
  amount
  description
  paymentReference
  paymentType
  approvedBy {
    ${userFields}
  }
  recordedBy {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    email
    firstName
    lastName
    username
    userType
    institutionUserType
    lastLogin
    institutionName
    departmentName
  }
  supportingDoc
`;

const ProjectContractorFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  institution {
    ${institutionFields}
  }
  companyName
  contractNumber
  contractType
  scopeOfWork
  contractAmount
  currency
  contractStart
  contractEnd
  actualEnd
  retentionPercent
  contactPerson {
    ${userFields}
  }
  status
`;

const projectInspectionFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  site {
    id
    uniqueId
    name
  }
  contractorLink {
    id
    uniqueId
    contractNumber
    contractType
    companyName
  }
  inspectionType
  inspectionDate
  inspector {
    ${userFields}
  }
  physicalProgress
  weatherConditions
  workersOnSite
  equipmentOnSite
  generalObservations
  compliances
  nonCompliances
  safetyObservations
  recommendations
  nextInspectionDate
  status
  photosAttached
`;

const minProjectInspectionFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  contractorLink {
    id
    uniqueId
    contractNumber
    contractType
    companyName
  }
  inspectionType
  inspectionDate
  inspector {
    ${userFields}
  }
  physicalProgress
  weatherConditions
  workersOnSite
  equipmentOnSite
  generalObservations
  compliances
  nonCompliances
  safetyObservations
  recommendations
  nextInspectionDate
  status
  photosAttached
`;

const projectQualityCheckFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  inspection {
    ${minProjectInspectionFields}
  }
  site {
    id
    uniqueId
    name
  }
  component {
    id
    uniqueId
    code
    title
  }
  checkType
  specificationRef
  testDate
  testedBy {
    ${userFields}
  }
  labOrLocation
  sampleReference
  requiredValue
  actualValue
  result
  actionsRequired
  closedBy {
    ${minUserFields}
  }
  closedDate
  status
`;

const projectHandoverFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  site {
    id
    uniqueId
    name
  }
  handoverType
  handoverDate
  handedOverBy {
    ${userFields}
  }
  receivedBy {
    ${minUserFields}
  }
  itemsHandedOver
  defectsNoted
  defectsDeadline
  certificateNumber
  status
  notes
`;

const ProjectReportingPeriodFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  name
  startDate
  endDate
  financialYear {
    ${financialYearFields}
  }
  status
`;

const projectReportingApprovalFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  report {
    id
    uniqueId
    reportType
    overallStatus
  }
  stage
  reviewedBy {
    ${userFields}
  }
  status
  comments
  reviewedAt
`;

const spIndicatorFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  spId
  objectiveId
  code
  name
  definition
  unit
  frequency
  dataCollectionFrequency
  baselineValue
  baselineYear
  dataSource
  meansOfVerification
  dataCollectionMethod
  responsibleDepartment {
    uniqueId
    name
  }
  responsibleSection {
    uniqueId
    name
  }
  progressDirection
  isCumulative
  sortOrder
  annualTargets {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    indicatorId
    financialYear {
      uniqueId
      code
      startYear
      endYear
      isCurrent
    }
    reportingPeriod
    targetValue
    actualValue
    meansOfVerification
    dataCollectedAt
    notes
  }
`;

const projectIndicatorFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  projectIndicator {
    id
    uniqueId
    code
    name
    indicatorType
  }
  spIndicator {
    ${spIndicatorFields}
  }
`;

const projectBeneficiariesTargetFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  beneficiaryType
  category
  description
  totalTarget
  maleTarget
  femaleTarget
  youthTarget
  disabledTarget
  region
  notes
`;

const projectBeneficiariesProgressFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  beneficiaryTarget {
    ${projectBeneficiariesTargetFields}
  }
  reportingPeriod {
    id
    uniqueId
    name
  }
  actualCount
  maleCount
  femaleCount
  youthCount
  disabledCount
  cumulativeCount
  narrative
  dataSource
  submittedBy {
    ${userFields}
  }
  status
`;

const projectIndicatorsFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  component {
    id
    uniqueId
    code
    title
  }
  code
  name
  definition
  indicatorType
  dataType
  scaleType
  unit
  baselineValue
  baselineYear
  targetValue
  frequency
  progressDirection
  dataSource
  meansOfVerification
  responsible {
    ${userFields}
  }
  isDisaggregated
`;

const projectIndicatorDataFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  indicator {
    id
    uniqueId
    code
    name
    indicatorType
  }
  reportingPeriod {
    id
    uniqueId
    name
  }
  actualValue
  targetValue
  qualitativeValue
  maleCount
  femaleCount
  youthCount
  disabledCount
  narrative
  dataQuality
  submittedBy {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    email
    firstName
    lastName
    username
    userType
    institutionUserType
    lastLogin
    role {
      id
      uniqueId
      createdAt
      updatedAt
      deletedAt
      isDeleted
      isActive
      name
      scope
      institutionId
      institutionName
      permissions {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        name
        code
        scope
      }
    }
    institutionName
    departmentName
    institution {
      ${institutionFields}
    }
  }
  submittedAt
  status
`;

const projectPriorChallengeFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  challenge
  rootCause
  area
  severity
  recommendation
  recommendedAction
  targetResolution
  isRecurring
  resolutionNotes
  resolvedDate
  status
  sortOrder
`;

const projectReportChallengeFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  report {
    id
    uniqueId
    reportType
    overallStatus
  }
  challenge
  rootCause
  area
  severity
  recommendation
  recommendedAction
  responsible {
    ${userFields}
  }
  targetResolution
  isRecurring
  priorChallenge {
    ${projectPriorChallengeFields}
  }
  resolutionNotes
  resolvedDate
  status
  sortOrder
`;

const projectProgressReportFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  reportingPeriod {
    id
    uniqueId
    name
  }
  reportType
  physicalProgress
  financialProgress
  scheduleStatus
  overallStatus
  achievements
  plannedNextPeriod
  submittedBy {
    ${minUserFields}
  }
  submittedAt
  status
  challenges {
    ${projectReportChallengeFields}
  }
`;

const challengesFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  report {
    id
    uniqueId
    reportType
    overallStatus
  }
  challenge
  rootCause
  area
  severity
  recommendation
  recommendedAction
  responsible {
    ${minUserFields}
  }
  targetResolution
  isRecurring
  priorChallenge {
    ${projectPriorChallengeFields}
  }
  resolutionNotes
  resolvedDate
  status
  sortOrder
`;

const progressReportFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  reportingPeriod {
    id
    uniqueId
    name
  }
  reportType
  physicalProgress
  financialProgress
  scheduleStatus
  overallStatus
  achievements
  plannedNextPeriod
  submittedBy {
    ${userFields}
  }
  submittedAt
  status
  challenges {
    ${challengesFields}
  }
`;

const projectRiskFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  title
  description
  riskCategory
  likelihood
  impact
  mitigationStrategy
  contingencyPlan
  riskOwner {
    ${userFields}
  }
  identifiedDate
  reviewDate
  status
`;

const projectIssueFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  relatedRisk {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    title
    description
    riskCategory
    likelihood
    impact
    mitigationStrategy
    contingencyPlan
    riskOwner {
      id
      uniqueId
      createdAt
      updatedAt
      deletedAt
      isDeleted
      isActive
      email
      firstName
      lastName
      username
      userType
      institutionUserType
      lastLogin
      institutionName
      departmentName
    }
    identifiedDate
    reviewDate
    status
  }
  title
  description
  severity
  issueDate
  reportedBy {
    ${userFields}
  }
  assignedTo {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    email
    firstName
    lastName
    username
    userType
    institutionUserType
    lastLogin
    institutionName
    departmentName
  }
  resolutionPlan
  resolutionDate
  resolutionNotes
  status
`;

const projectImpactAssessmentFields = `
  id
  uniqueId
  createdAt
  updatedAt
  deletedAt
  isDeleted
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  assessmentType
  title
  assessmentDate
  conductedBy {
    ${userFields}
  }
  externalAssessor
  scope
  methodology
  keyFindings
  outcomesAchieved
  outcomesNotAchieved
  unintendedOutcomes
  sustainabilityNotes
  lessonsLearned
  recommendations
  overallRating
  status
  reportUrl
`;

export const GET_PROJECT_PROGRESS_REPORTS = gql`
  query GetProjectProgressReports($filtering: ProjectProgressReportFilterInput) {
    getProjectProgressReports(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectProgressReportFields}
      }
    }
  }
`;


export const GET_PROJECTS = gql`
  query GetProjects($filtering: ProjectFilterInput) {
    getProjects(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectFields}
      }
    }
  }
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject($input: ProjectInput!) {
    createProject(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectFields}
      }
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateProject($input: ProjectInput!) {
    updateProject(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectFields}
      }
    }
  }
`;

export const DELETE_PROJECT = gql`
  mutation DeleteProject($uniqueId: String!) {
    deleteProject(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        uniqueId
      }
    }
  }
`;

export const CLOSE_PROJECT = gql`
  mutation CloseProject($uniqueId: String!, $actualEndDate: String!) {
    closeProject(uniqueId: $uniqueId, actualEndDate: $actualEndDate) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectFields}
      }
    }
  }
`;

export const CREATE_PROJECT_PHASE = gql`
  mutation CreateProjectPhase($input: ProjectPhaseInput!) {
    createProjectPhase(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectPhaseFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_PHASE = gql`
  mutation UpdateProjectPhase($input: ProjectPhaseInput!) {
    updateProjectPhase(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectPhaseFields}
      }
    }
  }
`;

export const DELETE_PROJECT_PHASE = gql`
  mutation DeleteProjectPhase($uniqueId: String!) {
    deleteProjectPhase(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        uniqueId
      }
    }
  }
`;

export const GET_PROJECT_PHASES = gql`
  query GetProjectPhases($projectId: String) {
    getProjectPhases(projectId: $projectId) {
      response {
       ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectPhaseFields}
      }
    }
  }
`;

export const CREATE_PROJECT_COMPONENTS = gql`
  mutation CreateProjectComponent($input: ProjectComponentInput!) {
    createProjectComponent(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectComponentFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_COMPONENTS = gql`
  mutation UpdateProjectComponent($input: ProjectComponentInput!) {
    updateProjectComponent(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectComponentFields}
      }
    }
  }
`;

export const DELETE_PROJECT_COMPONENTS = gql`
  mutation DeleteProjectComponent($uniqueId: String!) {
    deleteProjectComponent(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        uniqueId
      }
    }
  }
`;

export const GET_PROJECT_COMPONENTS = gql`
  query GetProjectComponents($filtering: ProjectComponentFilterInput) {
    getProjectComponents(filtering: $filtering) {
      response {
       ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectComponentFields}
      }
    }
  }
`;

export const GET_PROJECT_COMPONENT_PROGRESS = gql`
  query GetProjectComponentProgress($reportingPeriodId: String, $componentId: String, $projectId: String) {
    getProjectComponentProgress(reportingPeriodId: $reportingPeriodId, componentId: $componentId, projectId: $projectId) {
      response {
       ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectComponentProgressField}
      }
    }
  }
`;

export const CREATE_PROJECT_MILESTONE = gql`
  mutation CreateProjectMilestone($input: ProjectMilestoneInput!) {
    createProjectMilestone(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectMilestoneFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_MILESTONE = gql`
  mutation UpdateProjectMilestone($input: ProjectMilestoneInput!) {
    updateProjectMilestone(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectMilestoneFields}
      }
    }
  }
`;

export const DELETE_PROJECT_MILESTONE = gql`
  mutation DeleteProjectMilestone($uniqueId: String!) {
    deleteProjectMilestone(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        uniqueId
      }
    }
  }
`;

export const ACHIEVE_PROJECT_MILESTONE = gql`
  mutation AchieveProjectMilestone($uniqueId: String!, $actualDate: String!) {
    achieveProjectMilestone(uniqueId: $uniqueId, actualDate: $actualDate) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectMilestoneFields}
      }
    }
  }
`;

export const GET_PROJECT_MILESTONES = gql`
  query GetProjectMilestones($projectId: String, $phaseId: String, $componentId: String) {
    getProjectMilestones(projectId: $projectId, phaseId: $phaseId, componentId: $componentId) {
      response {
       ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectMilestoneFields}
      }
    }
  }
`;

export const CREATE_PROJECT_BUDGETLINE = gql`
  mutation AddProjectBudgetLine($input: ProjectBudgetLineInput!) {
    addProjectBudgetLine(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectBudgetlineFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_BUDGETLINE = gql`
  mutation UpdateProjectBudgetLine($input: ProjectBudgetLineInput!) {
    updateProjectBudgetLine(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectBudgetlineFields}
      }
    }
  }
`;

export const DELETE_PROJECT_BUDGETLINE = gql`
  mutation DeleteProjectBudgetLine($uniqueId: String!) {
    deleteProjectBudgetLine(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        uniqueId
      }
    }
  }
`;

export const GET_PROJECT_BUDGETLINE = gql`
  query GetProjectBudgetLines($projectId: String) {
    getProjectBudgetLines(projectId: $projectId) {
      response {
       ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectBudgetlineFields}
      }
    }
  }
`;

export const CREATE_PROJECT_EXPENDITURE = gql`
  mutation RecordProjectExpenditure($input: ProjectExpenditureInput!) {
    recordProjectExpenditure(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${ProjectExpenditureFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_EXPENDITURE = gql`
  mutation UpdateProjectExpenditure($input: ProjectExpenditureInput!) {
    updateProjectExpenditure(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${ProjectExpenditureFields}
      }
    }
  }
`;

export const DELETE_PROJECT_EXPENDITURE = gql`
  mutation DeleteProjectExpenditure($uniqueId: String!) {
    deleteProjectExpenditure(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        uniqueId
      }
    }
  }
`;

export const GET_PROJECT_EXPENDITURES = gql`
  query GetProjectExpenditures($contractorLinkId: String, $budgetLineId: String, $projectId: String) {
    getProjectExpenditures(contractorLinkId: $contractorLinkId, budgetLineId: $budgetLineId, projectId: $projectId) {
      response {
       ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${ProjectExpenditureFields}
      }
    }
  }
`;

export const CREATE_PROJECT_CONTRACTOR = gql`
  mutation AddProjectContractor($input: ProjectContractorInput!) {
    addProjectContractor(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${ProjectContractorFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_CONTRACTOR = gql`
  mutation UpdateProjectContractor($input: ProjectContractorInput!) {
    updateProjectContractor(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${ProjectContractorFields}
      }
    }
  }
`;

export const DELETE_PROJECT_CONTRACTOR = gql`
  mutation RemoveProjectContractor($uniqueId: String!) {
    removeProjectContractor(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        uniqueId
      }
    }
  }
`;

export const GET_PROJECT_CONTRACTORS = gql`
  query GetProjectContractors($projectId: String) {
    getProjectContractors(projectId: $projectId) {
      response {
       ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${ProjectContractorFields}
      }
    }
  }
`;

export const CREATE_PROJECT_INSPECTION = gql`
  mutation CreateProjectInspection($input: ProjectInspectionInput!) {
    createProjectInspection(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectInspectionFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_INSPECTION = gql`
  mutation UpdateProjectInspection($input: ProjectInspectionInput!) {
    updateProjectInspection(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectInspectionFields}
      }
    }
  }
`;

export const CLOSE_PROJECT_INSPECTION = gql`
  mutation CloseProjectInspection($uniqueId: String!) {
    closeProjectInspection(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectInspectionFields}
      }
    }
  }
`;

export const GET_PROJECT_INSPECTION = gql`
  query GetProjectInspections($filtering: ProjectInspectionFilterInput) {
    getProjectInspections(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectInspectionFields}
      }
    }
  }
`;

export const ADD_PROJECT_QUALITY_CHECKS = gql`
  mutation AddQualityCheck($input: ProjectQualityCheckInput!) {
    addQualityCheck(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectQualityCheckFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_QUALITY_CHECKS = gql`
  mutation UpdateQualityCheck($input: ProjectQualityCheckInput!) {
    updateQualityCheck(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectQualityCheckFields}
      }
    }
  }
`;

export const CLOSE_PROJECT_QUALITY_CHECKS = gql`
  mutation CloseQualityCheck($uniqueId: String!) {
    closeQualityCheck(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectQualityCheckFields}
      }
    }
  }
`;

export const GET_PROJECT_QUALITY_CHECKS = gql`
  query GetProjectQualityChecks($projectId: String, $result: String, $componentId: String) {
    getProjectQualityChecks(projectId: $projectId, result: $result, componentId: $componentId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectQualityCheckFields}
      }
    }
  }
`;

export const CREATE_PROJECT_HANDOVER = gql`
  mutation CreateProjectHandover($input: ProjectHandoverInput!) {
    createProjectHandover(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectHandoverFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_HANDOVER = gql`
  mutation UpdateProjectHandover($input: ProjectHandoverInput!) {
    updateProjectHandover(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectHandoverFields}
      }
    }
  }
`;

export const GET_PROJECT_HANDOVER = gql`
  query GetProjectHandovers($projectId: String) {
    getProjectHandovers(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectHandoverFields}
      }
    }
  }
`;

export const CREATE_PROJECT_REPORTING_PERIOD = gql`
  mutation CreateProjectReportingPeriod($input: ProjectReportingPeriodInput!) {
    createProjectReportingPeriod(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${ProjectReportingPeriodFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_REPORTING_PERIOD = gql`
  mutation UpdateProjectReportingPeriod($input: ProjectReportingPeriodInput!) {
    updateProjectReportingPeriod(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${ProjectReportingPeriodFields}
      }
    }
  }
`;

export const CLOSE_PROJECT_REPORTING_PERIOD = gql`
  mutation CloseProjectReportingPeriod($uniqueId: String!) {
    closeProjectReportingPeriod(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${ProjectReportingPeriodFields}
      }
    }
  }
`;

export const GET_PROJECT_REPORTING_PERIOD = gql`
  query GetProjectReportingPeriods($filtering: ProjectReportingPeriodFilterInput) {
    getProjectReportingPeriods(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${ProjectReportingPeriodFields}
      }
    }
  }
`;

export const CREATE_PROJECT_INDICATOR = gql`
  mutation CreateProjectIndicator($input: ProjectIndicatorInput!) {
    createProjectIndicator(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorsFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_INDICATOR = gql`
  mutation UpdateProjectIndicator($input: ProjectIndicatorInput!) {
    updateProjectIndicator(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorsFields}
      }
    }
  }
`;

export const DELETE_PROJECT_INDICATOR = gql`
  mutation DeleteProjectIndicator($uniqueId: String!) {
    deleteProjectIndicator(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorsFields}
      }
    }
  }
`;

export const SUMBIT_PROJECT_INDICATOR_DATA = gql`
  mutation SubmitProjectIndicatorData($input: ProjectIndicatorDataInput!) {
    submitProjectIndicatorData(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorDataFields}
      }
    }
  }
`;

export const REJECT_PROJECT_INDICATOR_DATA = gql`
  mutation RejectProjectIndicatorData($uniqueId: String!) {
    rejectProjectIndicatorData(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorDataFields}
      }
    }
  }
`;

export const APPROVE_PROJECT_INDICATOR_DATA = gql`
  mutation ApproveProjectIndicatorData($uniqueId: String!) {
    approveProjectIndicatorData(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorDataFields}
      }
    }
  }
`;

export const GET_PROJECT_INDICATOR_DATA = gql`
  query GetProjectIndicatorData($indicatorId: String, $reportingPeriodId: String) {
    getProjectIndicatorData(indicatorId: $indicatorId, reportingPeriodId: $reportingPeriodId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectIndicatorDataFields}
      }
    }
  }
`;

export const LINK_PROJECT_INDICATOR_TO_SP_INDICATOR = gql`
  mutation LinkProjectIndicatorToSpIndicator($input: ProjectIndicatorSpLinkInput!) {
    linkProjectIndicatorToSpIndicator(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorFields}
      }
    }
  }
`;
export const LINK_PROJECT_INDICATORS_TO_SP = LINK_PROJECT_INDICATOR_TO_SP_INDICATOR;

export const UNLINK_PROJECT_INDICATOR_FROM_SP_INDICATOR = gql`
  mutation UnlinkProjectIndicatorFromSpIndicator($uniqueId: String!) {
    unlinkProjectIndicatorFromSpIndicator(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIndicatorFields}
      }
    }
  }
`;
export const UNLINK_PROJECT_INDICATORS_TO_SP = UNLINK_PROJECT_INDICATOR_FROM_SP_INDICATOR;

export const GET_PROJECT_INDICATORS = gql`
  query GetProjectIndicators($filtering: ProjectIndicatorFilterInput) {
    getProjectIndicators(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectIndicatorsFields}
      }
    }
  }
`;

export const GET_PROJECT_INDICATORS_TO_SP = GET_PROJECT_INDICATORS;

export const GET_PROJECT_BENEFICIARIES_TARGET = gql`
  query GetProjectBeneficiaryTargets($projectId: String) {
    getProjectBeneficiaryTargets(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectBeneficiariesTargetFields}
      }
    }
  }
`;

export const GET_PROJECT_BENEFICIARIES_ACTUAL = gql`
  query GetProjectBeneficiaryActuals($targetId: String, $reportingPeriodId: String, $projectId: String) {
    getProjectBeneficiaryActuals(targetId: $targetId, reportingPeriodId: $reportingPeriodId, projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectBeneficiariesProgressFields}
      }
    }
  }
`;

export const CREATE_PROJECT_REPORT_APPROVAL = gql`
  mutation CreateProjectReportApproval($input: ProjectReportApprovalInput!) {
    createProjectReportApproval(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectReportingApprovalFields}
      }
    }
  }
`;

export const GET_PROJECT_REPORT_APPROVALS = gql`
  query GetProjectReportApprovals($reportId: String) {
    getProjectReportApprovals(reportId: $reportId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectReportingApprovalFields}
      }
    }
  }
`;

export const GET_OPEN_PROJECT_CHALLENGES = gql`
  query GetOpenProjectChallenges($projectId: String!) {
    getOpenProjectChallenges(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectReportChallengeFields}
      }
    }
  }
`;

export const GET_PROJECT_REPORT_CHALLENGES = gql`
  query GetProjectReportChallenges($reportId: String, $status: String, $projectId: String) {
    getProjectReportChallenges(reportId: $reportId, status: $status, projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectReportChallengeFields}
      }
    }
  }
`;

export const SUBMIT_PROJECT_PROGRESS_REPORT = gql`
  mutation SubmitProjectProgressReport($input: ProjectProgressReportInput!) {
    submitProjectProgressReport(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${progressReportFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_PROGRESS_REPORT = gql`
  mutation UpdateProjectProgressReport($input: ProjectProgressReportInput!) {
    updateProjectProgressReport(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${progressReportFields}
      }
    }
  }
`;

export const REJECT_PROJECT_PROGRESS_REPORT = gql`
  mutation RejectProjectProgressReport($uniqueId: String!) {
    rejectProjectProgressReport(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${progressReportFields}
      }
    }
  }
`;

export const APPROVE_PROJECT_PROGRESS_REPORT = gql`
  mutation ApproveProjectProgressReport($uniqueId: String!) {
    approveProjectProgressReport(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${progressReportFields}
      }
    }
  }
`;

export const ADD_PROGRESS_REPORT_CHALLENGE = gql`
  mutation AddProgressReportChallenge($input: ProjectProgressReportChallengeInput!) {
    addProgressReportChallenge(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectReportChallengeFields}
      }
    }
  }
`;

export const UPDATE_PROGRESS_REPORT_CHALLENGE = gql`
  mutation UpdateProgressReportChallenge($input: ProjectProgressReportChallengeInput!) {
    updateProgressReportChallenge(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectReportChallengeFields}
      }
    }
  }
`;

export const DELETE_PROGRESS_REPORT_CHALLENGE = gql`
  mutation DeleteProgressReportChallenge($uniqueId: String!) {
    deleteProgressReportChallenge(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectReportChallengeFields}
      }
    }
  }
`;

export const RESOLVE_PROGRESS_REPORT_CHALLENGE = gql`
  mutation ResolveProgressReportChallenge($uniqueId: String!, $resolutionNotes: String!) {
    resolveProgressReportChallenge(uniqueId: $uniqueId, resolutionNotes: $resolutionNotes) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectReportChallengeFields}
      }
    }
  }
`;

export const ADD_PROJECT_RISK = gql`
  mutation AddProjectRisk($input: ProjectRiskInput!) {
    addProjectRisk(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectRiskFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_RISK = gql`
  mutation UpdateProjectRisk($input: ProjectRiskInput!) {
    updateProjectRisk(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectRiskFields}
      }
    }
  }
`;

export const RESOLVE_PROJECT_RISK = gql`
  mutation ResolveProjectRisk($uniqueId: String!) {
    resolveProjectRisk(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectRiskFields}
      }
    }
  }
`;

export const GET_PROJECT_RISK = gql`
  query GetProjectRisks($filtering: ProjectRiskFilterInput) {
    getProjectRisks(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectRiskFields}
      }
    }
  }
`;

export const REPROT_PROJECT_ISSUE = gql`
  mutation ReportProjectIssue($input: ProjectIssueInput!) {
    reportProjectIssue(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIssueFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_ISSUE = gql`
  mutation UpdateProjectIssue($input: ProjectIssueInput!) {
    updateProjectIssue(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIssueFields}
      }
    }
  }
`;

export const RESOLVE_PROJECT_ISSUE = gql`
  mutation ResolveProjectIssue($uniqueId: String!, $resolutionNotes: String!) {
    resolveProjectIssue(uniqueId: $uniqueId, resolutionNotes: $resolutionNotes) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectIssueFields}
      }
    }
  }
`;

export const GET_PROJECT_ISSUES = gql`
  query GetProjectIssues($filtering: ProjectIssueFilterInput) {
    getProjectIssues(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectIssueFields}
      }
    }
  }
`;

export const CREATE_PROJECT_IMPACT_ASSESSMENT = gql`
  mutation CreateImpactAssessment($input: ProjectImpactAssessmentInput!) {
    createImpactAssessment(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectImpactAssessmentFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_IMPACT_ASSESSMENT = gql`
  mutation UpdateImpactAssessment($input: ProjectImpactAssessmentInput!) {
    updateImpactAssessment(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectImpactAssessmentFields}
      }
    }
  }
`;

export const FINALIZE_PROJECT_IMPACT_ASSESSMENT = gql`
  mutation FinalizeImpactAssessment($uniqueId: String!) {
    finalizeImpactAssessment(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectImpactAssessmentFields}
      }
    }
  }
`;

export const ADD_PROJECT_QUALITATIVE_ENTRY = gql`
  mutation AddQualitativeEntry($input: ProjectQualitativeEntryInput!) {
    addQualitativeEntry(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        reportingPeriod {
          id
          uniqueId
          name
        }
        component {
          id
          uniqueId
          code
          title
        }
        assessment {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          assessmentType
          title
          assessmentDate
          conductedBy {
            ${minUserFields}
          }
          externalAssessor
          scope
          methodology
          keyFindings
          outcomesAchieved
          outcomesNotAchieved
          unintendedOutcomes
          sustainabilityNotes
          lessonsLearned
          recommendations
          overallRating
          status
          reportUrl
        }
        entryType
        subject
        content
        source
        participantsCount
        sentiment
        rating
        recordedBy {
          ${userFields}
        }
        recordedDate
        location
        isVerified
        verifiedBy {
          ${minUserFields}
        }
      }
    }
  }
`;

export const UPDATE_PROJECT_QUALITATIVE_ENTRY = gql`
  mutation UpdateQualitativeEntry($input: ProjectQualitativeEntryInput!) {
    updateQualitativeEntry(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        reportingPeriod {
          id
          uniqueId
          name
        }
        component {
          id
          uniqueId
          code
          title
        }
        assessment {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          assessmentType
          title
          assessmentDate
          conductedBy {
            ${minUserFields}
          }
          externalAssessor
          scope
          methodology
          keyFindings
          outcomesAchieved
          outcomesNotAchieved
          unintendedOutcomes
          sustainabilityNotes
          lessonsLearned
          recommendations
          overallRating
          status
          reportUrl
        }
        entryType
        subject
        content
        source
        participantsCount
        sentiment
        rating
        recordedBy {
          ${userFields}
        }
        recordedDate
        location
        isVerified
        verifiedBy {
          ${minUserFields}
        }
      }
    }
  }
`;

export const DELETE_PROJECT_QUALITATIVE_ENTRY = gql`
  mutation DeleteQualitativeEntry($uniqueId: String!) {
    deleteQualitativeEntry(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        reportingPeriod {
          id
          uniqueId
          name
        }
        component {
          id
          uniqueId
          code
          title
        }
        assessment {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          assessmentType
          title
          assessmentDate
          conductedBy {
            ${minUserFields}
          }
          externalAssessor
          scope
          methodology
          keyFindings
          outcomesAchieved
          outcomesNotAchieved
          unintendedOutcomes
          sustainabilityNotes
          lessonsLearned
          recommendations
          overallRating
          status
          reportUrl
        }
        entryType
        subject
        content
        source
        participantsCount
        sentiment
        rating
        recordedBy {
          ${userFields}
        }
        recordedDate
        location
        isVerified
        verifiedBy {
          ${minUserFields}
        }
      }
    }
  }
`;

export const VERIFY_PROJECT_QUALITATIVE_ENTRY = gql`
  mutation VerifyQualitativeEntry($uniqueId: String!) {
    verifyQualitativeEntry(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        reportingPeriod {
          id
          uniqueId
          name
        }
        component {
          id
          uniqueId
          code
          title
        }
        assessment {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          assessmentType
          title
          assessmentDate
          conductedBy {
            ${minUserFields}
          }
          externalAssessor
          scope
          methodology
          keyFindings
          outcomesAchieved
          outcomesNotAchieved
          unintendedOutcomes
          sustainabilityNotes
          lessonsLearned
          recommendations
          overallRating
          status
          reportUrl
        }
        entryType
        subject
        content
        source
        participantsCount
        sentiment
        rating
        recordedBy {
          ${userFields}
        }
        recordedDate
        location
        isVerified
        verifiedBy {
          ${minUserFields}
        }
      }
    }
  }
`;

export const ADD_PROJECT_DOCUMENT = gql`
  mutation AddProjectDocument($input: ProjectDocumentInput!) {
    addProjectDocument(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        component {
          id
          uniqueId
          code
          title
        }
        inspection {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          site {
            id
            uniqueId
            name
          }
          contractorLink {
            id
            uniqueId
            contractNumber
            contractType
            companyName
          }
          inspectionType
          inspectionDate
          inspector {
            ${minUserFields}
          }
          physicalProgress
          weatherConditions
          workersOnSite
          equipmentOnSite
          generalObservations
          compliances
          nonCompliances
          safetyObservations
          recommendations
          nextInspectionDate
          status
          photosAttached
        }
        assessment {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          assessmentType
          title
          assessmentDate
          conductedBy {
            ${minUserFields}
          }
          externalAssessor
          scope
          methodology
          keyFindings
          outcomesAchieved
          outcomesNotAchieved
          unintendedOutcomes
          sustainabilityNotes
          lessonsLearned
          recommendations
          overallRating
          status
          reportUrl
        }
        documentType
        title
        description
        fileUrl
        fileSizeKb
        capturedDate
        latitude
        longitude
        uploadedBy {
          ${userFields}
        }
        uploadedAt
        isPublic
      }
    }
  }
`;

export const UPDATE_PROJECT_DOCUMENT = gql`
  mutation UpdateProjectDocument($input: ProjectDocumentInput!) {
    updateProjectDocument(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        component {
          id
          uniqueId
          code
          title
        }
        inspection {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          site {
            id
            uniqueId
            name
          }
          contractorLink {
            id
            uniqueId
            contractNumber
            contractType
            companyName
          }
          inspectionType
          inspectionDate
          inspector {
            ${minUserFields}
          }
          physicalProgress
          weatherConditions
          workersOnSite
          equipmentOnSite
          generalObservations
          compliances
          nonCompliances
          safetyObservations
          recommendations
          nextInspectionDate
          status
          photosAttached
        }
        assessment {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          assessmentType
          title
          assessmentDate
          conductedBy {
            ${minUserFields}
          }
          externalAssessor
          scope
          methodology
          keyFindings
          outcomesAchieved
          outcomesNotAchieved
          unintendedOutcomes
          sustainabilityNotes
          lessonsLearned
          recommendations
          overallRating
          status
          reportUrl
        }
        documentType
        title
        description
        fileUrl
        fileSizeKb
        capturedDate
        latitude
        longitude
        uploadedBy {
          ${userFields}
        }
        uploadedAt
        isPublic
      }
    }
  }
`;

export const DELETE_PROJECT_DOCUMENT = gql`
  mutation DeleteProjectDocument($uniqueId: String!) {
    deleteProjectDocument(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        component {
          id
          uniqueId
          code
          title
        }
        inspection {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          site {
            id
            uniqueId
            name
          }
          contractorLink {
            id
            uniqueId
            contractNumber
            contractType
            companyName
          }
          inspectionType
          inspectionDate
          inspector {
            ${minUserFields}
          }
          physicalProgress
          weatherConditions
          workersOnSite
          equipmentOnSite
          generalObservations
          compliances
          nonCompliances
          safetyObservations
          recommendations
          nextInspectionDate
          status
          photosAttached
        }
        assessment {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          assessmentType
          title
          assessmentDate
          conductedBy {
            ${minUserFields}
          }
          externalAssessor
          scope
          methodology
          keyFindings
          outcomesAchieved
          outcomesNotAchieved
          unintendedOutcomes
          sustainabilityNotes
          lessonsLearned
          recommendations
          overallRating
          status
          reportUrl
        }
        documentType
        title
        description
        fileUrl
        fileSizeKb
        capturedDate
        latitude
        longitude
        uploadedBy {
          ${userFields}
        }
        uploadedAt
        isPublic
      }
    }
  }
`;

export const ADD_PROJECT_PROJECT_MEMBER = gql`
  mutation AddProjectTeamMember($input: ProjectTeamMemberInput!) {
    addProjectTeamMember(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        profile {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          email
          firstName
          lastName
          username
          userType
          institutionUserType
          lastLogin
          role {
            id
            uniqueId
            createdAt
            updatedAt
            deletedAt
            isDeleted
            isActive
            name
            scope
            institutionId
            institutionName
            permissions {
              id
              uniqueId
              createdAt
              updatedAt
              deletedAt
              isDeleted
              isActive
              name
              code
              scope
            }
          }
          institutionName
          departmentName
        }
        role
        institution {
          ${institutionFields}
        }
        assignedDate
        endDate
        isLead
        notes
      }
    }
  }
`;

export const UPDATE_PROJECT_PROJECT_MEMBER = gql`
  mutation UpdateProjectTeamMember($input: ProjectTeamMemberInput!) {
    updateProjectTeamMember(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        profile {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          email
          firstName
          lastName
          username
          userType
          institutionUserType
          lastLogin
          role {
            id
            uniqueId
            createdAt
            updatedAt
            deletedAt
            isDeleted
            isActive
            name
            scope
            institutionId
            institutionName
            permissions {
              id
              uniqueId
              createdAt
              updatedAt
              deletedAt
              isDeleted
              isActive
              name
              code
              scope
            }
          }
          institutionName
          departmentName
        }
        role
        institution {
          ${institutionFields}
        }
        assignedDate
        endDate
        isLead
        notes
      }
    }
  }
`;

export const REMOVE_PROJECT_PROJECT_MEMBER = gql`
  mutation RemoveProjectTeamMember($uniqueId: String!) {
    removeProjectTeamMember(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        profile {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          email
          firstName
          lastName
          username
          userType
          institutionUserType
          lastLogin
          role {
            id
            uniqueId
            createdAt
            updatedAt
            deletedAt
            isDeleted
            isActive
            name
            scope
            institutionId
            institutionName
            permissions {
              id
              uniqueId
              createdAt
              updatedAt
              deletedAt
              isDeleted
              isActive
              name
              code
              scope
            }
          }
          institutionName
          departmentName
        }
        role
        institution {
          ${institutionFields}
        }
        assignedDate
        endDate
        isLead
        notes
      }
    }
  }
`;

export const GET_PROJECT_TEAM_MEMBERS = gql`
  query GetProjectTeamMembers($projectId: String) {
    getProjectTeamMembers(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        profile {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          email
          firstName
          lastName
          username
          userType
          institutionUserType
          lastLogin
          role {
            id
            uniqueId
            createdAt
            updatedAt
            deletedAt
            isDeleted
            isActive
            name
            scope
            institutionId
            institutionName
            permissions {
              id
              uniqueId
              createdAt
              updatedAt
              deletedAt
              isDeleted
              isActive
              name
              code
              scope
            }
          }
          institutionName
          departmentName
        }
        role
        institution {
          ${institutionFields}
        }
        assignedDate
        endDate
        isLead
        notes
      }
    }
  }
`;

export const LINK_PROJECT_TO_SP_TARGET = gql`
  mutation LinkProjectToSpTarget($input: ProjectSpTargetLinkInput!) {
    linkProjectToSpTarget(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        target {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          objectiveId
          institution {
            uniqueId
            name
            shortForm
          }
          department {
            uniqueId
            name
          }
          code
          description
          unit
          progressDirection
          baselineValue
          baselineYear
          targetValue
          deadline
          sortOrder
        }
        contributionType
        contributionUnits
        contributionNotes
      }
    }
  }
`;

export const UNLINK_PROJECT_FROM_SP_TARGET = gql`
  mutation UnlinkProjectFromSpTarget($uniqueId: String!) {
    unlinkProjectFromSpTarget(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        target {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          objectiveId
          institution {
            uniqueId
            name
            shortForm
          }
          department {
            uniqueId
            name
          }
          code
          description
          unit
          progressDirection
          baselineValue
          baselineYear
          targetValue
          deadline
          sortOrder
        }
        contributionType
        contributionUnits
        contributionNotes
      }
    }
  }
`;

export const LINK_PROJECT_TO_FRAMEWORK = gql`
  mutation LinkProjectToFramework($input: ProjectFrameworkLinkInput!) {
    linkProjectToFramework(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        framework {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          name
          acronym
          frameworkType
          description
          startYear
          endYear
          coverageLevel
          status
          nodeCount
          levelOrder
        }
        contributionNotes
      }
    }
  }
`;

export const UNLINK_PROJECT_FROM_FRAMEWORK = gql`
  mutation UnlinkProjectFromFramework($uniqueId: String!) {
    unlinkProjectFromFramework(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        framework {
          id
          uniqueId
          createdAt
          updatedAt
          deletedAt
          isDeleted
          isActive
          name
          acronym
          frameworkType
          description
          startYear
          endYear
          coverageLevel
          status
          nodeCount
          levelOrder
        }
        contributionNotes
      }
    }
  }
`;
export const UNLINK_PROJECT_TO_FRAMEWORK = UNLINK_PROJECT_FROM_FRAMEWORK;

const projectQualitativeEntryFields = `
  id
  uniqueId
  createdAt
  updatedAt
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  reportingPeriod {
    id
    uniqueId
    name
  }
  component {
    id
    uniqueId
    code
    title
  }
  assessment {
    id
    uniqueId
    assessmentType
    title
    assessmentDate
  }
  entryType
  subject
  content
  source
  participantsCount
  sentiment
  rating
  recordedBy {
    ${minUserFields}
  }
  recordedDate
  location
  isVerified
  verifiedBy {
    ${minUserFields}
  }
`;

const projectDocumentFields = `
  id
  uniqueId
  createdAt
  updatedAt
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  component {
    id
    uniqueId
    code
    title
  }
  assessment {
    id
    uniqueId
    assessmentType
    title
  }
  documentType
  title
  description
  fileUrl
  fileSizeKb
  capturedDate
  latitude
  longitude
  uploadedBy {
    ${minUserFields}
  }
  uploadedAt
  isPublic
`;

const projectSpLinkFields = `
  id
  uniqueId
  createdAt
  updatedAt
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  strategicPlan {
    id
    uniqueId
    createdAt
    updatedAt
    deletedAt
    isDeleted
    isActive
    institution {
      uniqueId
      name
      shortForm
    }
    name
    planType
    vision
    mission
    developmentObjective
    startDate
    endDate
    approvedAt
  }
  contributionNotes
`;

const projectSpTargetLinkFields = `
  id
  uniqueId
  createdAt
  updatedAt
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  target {
    id
    uniqueId
    objectiveId
    institution {
      uniqueId
      name
      shortForm
    }
    code
    description
    unit
    targetValue
    deadline
  }
  contributionType
  contributionUnits
  contributionNotes
`;

const projectFrameworkLinkFields = `
  id
  uniqueId
  createdAt
  updatedAt
  isActive
  project {
    id
    uniqueId
    name
    code
    status
  }
  framework {
    id
    uniqueId
    name
    acronym
    frameworkType
    description
    startYear
    endYear
    coverageLevel
    status
  }
  contributionNotes
`;

export const UPDATE_PROJECT_SP_TARGET_LINK = gql`
  mutation UpdateProjectSpTargetLink($input: UpdateProjectSpTargetLinkInput!) {
    updateProjectSpTargetLink(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectSpTargetLinkFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_FRAMEWORK_LINK = gql`
  mutation UpdateProjectFrameworkLink($input: UpdateProjectFrameworkLinkInput!) {
    updateProjectFrameworkLink(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectFrameworkLinkFields}
      }
    }
  }
`;

export const LINK_PROJECT_TO_SP = gql`
  mutation LinkProjectToSp($input: ProjectSpLinkInput!) {
    linkProjectToSp(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectSpLinkFields}
      }
    }
  }
`;

export const UPDATE_PROJECT_SP_LINK = gql`
  mutation UpdateProjectSpLink($input: UpdateProjectSpLinkInput!) {
    updateProjectSpLink(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectSpLinkFields}
      }
    }
  }
`;

export const UNLINK_PROJECT_FROM_SP = gql`
  mutation UnlinkProjectFromSp($uniqueId: String!) {
    unlinkProjectFromSp(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        ${projectSpLinkFields}
      }
    }
  }
`;

export const GET_PROJECT_SP_LINKS = gql`
  query GetProjectSpLinks($projectId: String, $spId: String) {
    getProjectSpLinks(projectId: $projectId, spId: $spId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectSpLinkFields}
      }
    }
  }
`;

export const GET_PROJECT_IMPACT_ASSESSMENTS = gql`
  query GetProjectImpactAssessments($projectId: String) {
    getProjectImpactAssessments(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectImpactAssessmentFields}
      }
    }
  }
`;

export const GET_PROJECT_QUALITATIVE_ENTRIES = gql`
  query GetProjectQualitativeEntries($filtering: ProjectQualitativeEntryFilterInput) {
    getProjectQualitativeEntries(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectQualitativeEntryFields}
      }
    }
  }
`;

export const GET_PROJECT_DOCUMENTS = gql`
  query GetProjectDocuments($projectId: String, $documentType: String) {
    getProjectDocuments(projectId: $projectId, documentType: $documentType) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectDocumentFields}
      }
    }
  }
`;

export const GET_PROJECT_SP_TARGET_LINKS = gql`
  query GetProjectSpTargetLinks($projectId: String, $targetId: String) {
    getProjectSpTargetLinks(projectId: $projectId, targetId: $targetId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectSpTargetLinkFields}
      }
    }
  }
`;

export const GET_PROJECT_FRAMEWORK_LINKS = gql`
  query GetProjectFrameworkLinks($projectId: String) {
    getProjectFrameworkLinks(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        ${projectFrameworkLinkFields}
      }
    }
  }
`;

export const ISSUE_PAYMENT_CERTIFICATE = gql`
  mutation IssuePaymentCertificate($input: ProjectPaymentCertificateInput!) {
    issuePaymentCertificate(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        contractorLink {
          id
          uniqueId
          contractNumber
          contractType
          companyName
        }
        certificateNumber
        certificateType
        periodFrom
        periodTo
        workCertifiedValue
        cumulativeCertified
        retentionDeducted
        previousPayments
        netPaymentDue
        certifiedBy {
          ${userFields}
        }
        approvedBy {
          ${minUserFields}
        }
        certificateDate
        paymentDate
        status
      }
    }
  }
`;

export const APPROVE_PAYMENT_CERTIFICATE = gql`
  mutation ApprovePaymentCertificate($uniqueId: String!) {
    approvePaymentCertificate(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        contractorLink {
          id
          uniqueId
          contractNumber
          contractType
          companyName
        }
        certificateNumber
        certificateType
        periodFrom
        periodTo
        workCertifiedValue
        cumulativeCertified
        retentionDeducted
        previousPayments
        netPaymentDue
        certifiedBy {
          ${userFields}
        }
        approvedBy {
          ${minUserFields}
        }
        certificateDate
        paymentDate
        status
      }
    }
  }
`;

export const GET_PROJECT_PAYMENT_CERTIFICATES = gql`
  query GetProjectPaymentCertificates($contractorLinkId: String) {
    getProjectPaymentCertificates(contractorLinkId: $contractorLinkId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        contractorLink {
          id
          uniqueId
          contractNumber
          contractType
          companyName
        }
        certificateNumber
        certificateType
        periodFrom
        periodTo
        workCertifiedValue
        cumulativeCertified
        retentionDeducted
        previousPayments
        netPaymentDue
        certifiedBy {
          ${userFields}
        }
        approvedBy {
          ${minUserFields}
        }
        certificateDate
        paymentDate
        status
      }
    }
  }
`;

export const ADD_PROJECT_SITE = gql`
  mutation AddProjectSite($input: ProjectSiteInput!) {
    addProjectSite(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        name
        description
        region
        district
        ward
        village
        latitude
        longitude
        areaSqm
        lengthKm
        quantityUnits
        plannedQuantity
        isPrimary
        status
      }
    }
  }
`;

export const UPDATE_PROJECT_SITE = gql`
  mutation UpdateProjectSite($input: ProjectSiteInput!) {
    updateProjectSite(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        name
        description
        region
        district
        ward
        village
        latitude
        longitude
        areaSqm
        lengthKm
        quantityUnits
        plannedQuantity
        isPrimary
        status
      }
    }
  }
`;

export const GET_PROJECT_SITES = gql`
  query GetProjectSites($projectId: String) {
    getProjectSites(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        name
        description
        region
        district
        ward
        village
        latitude
        longitude
        areaSqm
        lengthKm
        quantityUnits
        plannedQuantity
        isPrimary
        status
      }
    }
  }
`;

export const REMOVE_PROJECT_SITE = gql`
  mutation RemoveProjectSite($uniqueId: String!) {
    removeProjectSite(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        name
        description
        region
        district
        ward
        village
        latitude
        longitude
        areaSqm
        lengthKm
        quantityUnits
        plannedQuantity
        isPrimary
        status
      }
    }
  }
`;

export const GET_WARDS = gql`
  query GetWards($filtering: WardFilterInput) {
    getWards(filtering: $filtering) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        name
        napaId
        code
      }
    }
  }
`;

export const ADD_PROJECT_FUNDING_SOURCE = gql`
  mutation AddProjectFundingSource($input: ProjectFundingSourceInput!) {
    addProjectFundingSource(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        sourceType
        funderName
        fundingReference
        amount
        currency
        percentage
        disbursementStart
        disbursementEnd
        conditions
        notes
      }
    }
  }
`;

export const UPDATE_PROJECT_FUNDING_SOURCE = gql`
  mutation UpdateProjectFundingSource($input: ProjectFundingSourceInput!) {
    updateProjectFundingSource(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        sourceType
        funderName
        fundingReference
        amount
        currency
        percentage
        disbursementStart
        disbursementEnd
        conditions
        notes
      }
    }
  }
`;

export const REMOVE_PROJECT_FUNDING_SOURCE = gql`
  mutation RemoveProjectFundingSource($uniqueId: String!) {
    removeProjectFundingSource(uniqueId: $uniqueId) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        sourceType
        funderName
        fundingReference
        amount
        currency
        percentage
        disbursementStart
        disbursementEnd
        conditions
        notes
      }
    }
  }
`;

export const GET_PROJECT_FUNDING_SOURCE = gql `
  query GetProjectFundingSources($projectId: String) {
    getProjectFundingSources(projectId: $projectId) {
      response {
        ${baseResponseFields}
      }
      page {
        ${basePageableGraphqlField}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        project {
          id
          uniqueId
          name
          code
          status
        }
        sourceType
        funderName
        fundingReference
        amount
        currency
        percentage
        disbursementStart
        disbursementEnd
        conditions
        notes
      }
    }
  }
`;

export const SUBMIT_COMPONENT_PROGRESS = gql`
  mutation SubmitComponentProgress($input: ProjectComponentProgressInput!) {
    submitComponentProgress(input: $input) {
      response {
        ${baseResponseFields}
      }
      data {
        id
        uniqueId
        createdAt
        updatedAt
        deletedAt
        isDeleted
        isActive
        component {
          id
          uniqueId
          code
          title
        }
        reportingPeriod {
          id
          uniqueId
          name
        }
        progressPercent
        actualQuantity
        narrative
        challenges
        source
        submittedBy {
          ${userFields}
        }
        status
      }
    }
  }
`;